"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Info, Loader2, Pencil, Plus, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@stockline/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@stockline/ui/components/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@stockline/ui/components/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@stockline/ui/components/tooltip";
import {
  parseSections,
  SectionedEditor,
  type Section as ParsedSection,
  type SectionedEditorHandle,
} from "@/components/markdown-sectioned-editor";

function estimateTokens(value: string): number {
  return Math.ceil(value.length / 4);
}

// Approximate token count + non-technical tooltip. Display only — no
// save gate. The exact tokenizer depends on the model, so we use the
// industry rule of thumb (1 token ≈ 4 chars) and label it as approximate.
function TokenIndicator({ value }: Readonly<{ value: string }>) {
  const { t } = useTranslation();
  const tokens = estimateTokens(value);
  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="tabular-nums">
          {t("common.aiManagement.tokenCount", { count: tokens })}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={t("common.aiManagement.tokenInfoAria")}
              className="text-muted-foreground/70 hover:text-foreground"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
            {t("common.aiManagement.tokenInfo")}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

/** Compact snippet of a body for the preview: collapses inner whitespace
 *  to single spaces so multi-line bodies read as one paragraph. Visual
 *  truncation is left to `line-clamp-2` on the render node — we no longer
 *  hard-cap the length here (that silently hid the tail of long bodies on
 *  wide panels); CSS clamps to two lines and adds the ellipsis. */
function firstLine(body: string): string {
  return body.replace(/\s+/g, " ").trim();
}

/** One preview row: badge + title + (optional) body snippet. Title and
 *  snippet wrap — capped at 2 lines via `line-clamp-2` — so narrow
 *  panels don't lose information to ellipsis. `min-w-0` on the inner
 *  column + `break-words` on the text nodes ensure long unbroken
 *  strings (URLs, tokens) wrap instead of forcing horizontal scroll.
 *
 *  Visual spec: title rendered semibold and slightly bigger so the body
 *  snippet underneath reads as a distinct caption — without that, the
 *  first word of the body bleeds into the title when both wrap to one
 *  line each and there's no margin between them. */
function PreviewRow({
  badge,
  titleNode,
  bodySnippet,
}: Readonly<{
  badge: React.ReactNode;
  titleNode: React.ReactNode;
  bodySnippet?: string;
}>) {
  return (
    <div className="flex w-full items-start gap-1.5">
      {badge}
      {/* Use a flex column with an explicit gap so the title and the
          body snippet can never visually concatenate into one line, no
          matter the font metrics (italic descenders / line-height
          shifts were making the body's first word read as part of the
          title before this). `display: block` on each child is forced
          by the flex container so line-clamp boxes never re-flow into
          each other. */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Italic + light weight on every title (real sections and the
            "General" label) — unified styling so the row reads as a
            consistent index entry, not a mix of UI button and content. */}
        <div className="line-clamp-2 block break-words text-sm font-normal italic leading-5 text-foreground">
          {titleNode}
        </div>
        {bodySnippet && (
          <div className="line-clamp-2 block break-words text-xs font-normal not-italic leading-4 text-muted-foreground">
            {bodySnippet}
          </div>
        )}
      </div>
    </div>
  );
}

/** Body of the preview card — used by both the interactive and the
 *  read-only branches in `ContextEditor` so each can render its own
 *  outer element with the right a11y attributes. */
function PreviewBody({
  blocks,
  hasContent,
  placeholder,
  previewNumberById,
  isReadOnly,
}: Readonly<{
  blocks: ParsedSection[];
  hasContent: boolean;
  placeholder: string;
  previewNumberById: Map<string, number>;
  isReadOnly: boolean;
}>) {
  const { t } = useTranslation();
  if (!hasContent) {
    return (
      <div className="flex min-h-[120px] items-start px-3 py-2">
        <span className="italic text-muted-foreground">
          {isReadOnly ? placeholder : t("common.aiManagement.emptyPreview")}
        </span>
      </div>
    );
  }
  return (
    <div className="persistent-scroll flex max-h-[200px] flex-col gap-1.5 overflow-y-auto px-3 py-2">
      {blocks.map((s) =>
        s.kind === "general" ? (
          <PreviewRow
            key={s.localId}
            badge={
              <span className="mt-[3px] inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                —
              </span>
            }
            titleNode={
              <span className="italic text-muted-foreground">
                {t("common.aiManagement.sections.generalLabel")}
              </span>
            }
            bodySnippet={firstLine(s.body)}
          />
        ) : (
          <PreviewRow
            key={s.localId}
            badge={
              <span className="mt-[3px] inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-violet-500/10 px-1 text-[10px] font-medium text-violet-600">
                {previewNumberById.get(s.localId) ?? ""}
              </span>
            }
            titleNode={
              s.title.trim() ? (
                <span>{s.title}</span>
              ) : (
                <span className="text-muted-foreground">
                  {t("common.aiManagement.sections.titlePlaceholder")}
                </span>
              )
            }
            bodySnippet={firstLine(s.body)}
          />
        ),
      )}
    </div>
  );
}

type ContextEditorProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onDiscard: () => void;
  isDirty: boolean;
  isSaving: boolean;
  isReadOnly?: boolean;
  description: string;
  placeholder: string;
  title: string;
  layout?: "stacked" | "grid";
  /** Notified when the expand dialog opens/closes so the parent can dim. */
  onExpandOpenChange?: (open: boolean) => void;
}>;

export function ContextEditor({
  value,
  onChange,
  onSave,
  onDiscard,
  isDirty,
  isSaving,
  isReadOnly = false,
  description,
  placeholder,
  title,
  layout = "stacked",
  onExpandOpenChange,
}: ContextEditorProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const canSave = isDirty && !isSaving;

  const setExpand = useCallback(
    (open: boolean) => {
      setExpanded(open);
      onExpandOpenChange?.(open);
    },
    [onExpandOpenChange],
  );

  // Close requests (ESC / overlay-click / close button) go through this so
  // the user gets a single "discard?" prompt when their edits are dirty.
  // The Cancel button in the footer already calls `onDiscard()` explicitly,
  // so it stays exempt from the prompt.
  const [discardOpen, setDiscardOpen] = useState(false);
  const requestExpand = useCallback(
    (open: boolean) => {
      if (!open && isDirty) {
        setDiscardOpen(true);
        return;
      }
      setExpand(open);
    },
    [isDirty, setExpand],
  );

  const confirmDiscard = useCallback(() => {
    setDiscardOpen(false);
    onDiscard();
    setExpand(false);
  }, [onDiscard, setExpand]);

  // Ref to drive the SectionedEditor's "add section" action from the
  // dialog header — saves a row inside the editor body.
  const editorRef = useRef<SectionedEditorHandle>(null);

  // Auto-close the dialog when a save completes successfully: detect
  // the isSaving true→false transition while `isDirty` flipped back to
  // false (which only happens on success; an error leaves it dirty).
  const prevSavingRef = useRef(isSaving);
  useEffect(() => {
    if (prevSavingRef.current && !isSaving && !isDirty) {
      setExpand(false);
    }
    prevSavingRef.current = isSaving;
  }, [isSaving, isDirty, setExpand]);

  // Preview shows the structural skeleton, not the full body. We render
  // only section titles (and the first short body slice for the leading
  // "general" block, since titles aren't available there) plus a footer
  // counting any hidden sections. Metadata HTML comments are stripped
  // unconditionally — they exist for round-tripping, not display.
  const previewBlocks = useMemo(() => {
    // Match the exact machine-generated metadata comment (single spaces, see
    // markdown-sectioned-editor.tsx) plus any trailing whitespace. Fixed
    // literals + a single trailing `\s*` avoid the super-linear backtracking a
    // leading `\s*<!--` would cause when scanned globally (Sonar S8786).
    const stripped = value.replace(
      / ?<!-- created="[^"]*" updated="[^"]*" -->\s*/g,
      "",
    );
    const sections = parseSections(stripped);
    return sections;
  }, [value]);

  // Show every section in the preview — long contexts scroll inside the
  // bounded card (see `max-h` + `overflow-y-auto` below). Slicing was
  // hiding content from the user; scrolling keeps everything reachable
  // without expanding the panel itself.
  const visiblePreview = previewBlocks;
  const hasContent = previewBlocks.length > 0;

  // Number titled sections only — "general" recovery blocks are excluded
  // from the count so a leading general block doesn't push the first
  // real section's badge to "2".
  const previewNumberById = useMemo(() => {
    const map = new Map<string, number>();
    let n = 0;
    for (const block of previewBlocks) {
      if (block.kind === "section") {
        n += 1;
        map.set(block.localId, n);
      }
    }
    return map;
  }, [previewBlocks]);

  // Preview — read-only rendered markdown. Click anywhere → open the
  // editor dialog. The textarea no longer lives in the panel itself,
  // which kills the "two surfaces, same focus level" problem.
  return (
    <div
      className={cn("flex flex-col gap-2", layout === "grid" && "lg:h-full")}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-muted-foreground flex-1">{description}</p>
        {!isReadOnly && (
          <button
            type="button"
            onClick={() => setExpand(true)}
            // Blue-tinted CTA so the only edit affordance for an otherwise
            // read-only preview is obvious — was washing out as a neutral
            // border button that visually merged with the description row.
            className="inline-flex items-center gap-1 rounded-md border border-blue-500/50 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:border-blue-500 hover:bg-blue-100"
            aria-label={t("common.aiManagement.openEditor")}
            title={t("common.aiManagement.openEditor")}
            data-testid="context-editor-edit"
          >
            <Pencil className="h-3 w-3" />
            {t("common.aiManagement.edit")}
          </button>
        )}
      </div>

      {/* Preview card is purely a display surface — no role / tabIndex /
          click handler. Edit-to-open lives on the explicit blue "Edit"
          button rendered above (see header row). This avoids:
            - a11y rule violations (S6819 wants a native <button>; S6848
              forbids interactive handlers on a non-interactive element);
            - a `<button>` wrapping a scroll container, which would fire
              synthetic clicks on scrollbar drag and open the editor on
              every scroll attempt. */}
      <div
        className={cn(
          "rounded-md border border-border bg-muted/30 text-left text-sm text-foreground",
          isReadOnly && "cursor-not-allowed opacity-80",
          layout === "grid" && "lg:flex-1",
        )}
        aria-disabled={isReadOnly || undefined}
      >
        <PreviewBody
          blocks={visiblePreview}
          hasContent={hasContent}
          placeholder={placeholder}
          previewNumberById={previewNumberById}
          isReadOnly={isReadOnly}
        />
      </div>

      {!isReadOnly && (
        <div className="flex items-center justify-between">
          <TokenIndicator value={value} />
        </div>
      )}

      <Dialog open={expanded} onOpenChange={requestExpand}>
        <DialogContent
          // z-index above the AI management side panel (z-[100]) so the
          // editor isn't trapped behind its host. The overlay is bumped to
          // z-[140] so it actually dims the side panel. Dialog fills most
          // of the viewport so the section list feels like a real editor.
          className="z-[150] flex h-[95vh] max-h-[95vh] w-[96vw] max-w-5xl flex-col gap-3 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-200"
          overlayClassName="z-[140] bg-black/40"
        >
          {/* Header row: title on the left, "+ Add section" on the right
              (sits below the dialog's close X). Description tucks under
              the title so the header occupies just two compact rows. */}
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-8">
              <div className="min-w-0 flex-1">
                <DialogTitle className="truncate text-base">
                  {title}
                </DialogTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {description}
                </p>
              </div>
              {!isReadOnly && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => editorRef.current?.addSection()}
                  className="h-7 shrink-0 gap-1.5 rounded-md text-xs"
                  data-testid="context-editor-add-section"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t("common.aiManagement.sections.addSection")}
                </Button>
              )}
            </div>
          </DialogHeader>

          <div className="flex min-h-[60vh] flex-1 flex-col">
            <SectionedEditor
              ref={editorRef}
              value={value}
              onChange={onChange}
              isReadOnly={isReadOnly}
            />
          </div>

          {!isReadOnly && (
            <div className="flex items-center justify-between gap-2">
              <TokenIndicator value={value} />
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onDiscard();
                    setExpand(false);
                  }}
                  disabled={isSaving}
                  className="h-8 rounded-md px-3"
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={onSave}
                  disabled={!canSave}
                  // "Light inside" save: soft primary tint + primary border
                  // + primary text, so it reads as the affirmative action
                  // without screaming like a solid filled button would.
                  className="h-8 min-w-[96px] gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 text-primary shadow-none hover:bg-primary/20"
                  data-testid="context-editor-save"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {t("common.saving")}
                    </>
                  ) : (
                    <>
                      <Save className="h-3.5 w-3.5" />
                      {t("common.save")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent
          className="z-[160] max-w-md"
          overlayClassName="z-[155]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("common.discardChanges.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.discardChanges.body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>
              {t("common.discardChanges.keepEditing")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDiscard}
              className="border border-destructive/40 bg-destructive/10 text-destructive shadow-none hover:bg-destructive/20 hover:text-destructive"
            >
              {t("common.discardChanges.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
